
import React, { useState, useEffect, useContext } from "react";
import {
  User,
  Phone,
  Mail,
  MapPin,
  Shield,
  Clock,
  Hospital,
} from "lucide-react";
import Navbar from "../components/Navbar";
import languageContext from "../context/language/languageContext";
import API from "../api";
import { useNavigate } from "react-router-dom";

const DoctorProfile = () => {
  const { setSessionTimeout, setAlert, setMessage } =
    useContext(languageContext);

  const [doctor, setDoctor] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const navigate = useNavigate();

  const getDoctorDetails = async () => {
    try {
      const res = await API.post("/show_profile");
      setDoctor(res.data?.doctor_details || {});
    } catch (error) {
      if (error.response?.data?.detail === "Token has expired") {
        setSessionTimeout(true);
      } else {
        setAlert(true);
        setMessage("Something went wrong");
      }
    }
  };

  const getSubscription = async () => {
    try {
      const res = await API.post("/subscription");
      setSubscription(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const getDaysLeft = (expiryDate) => {
    if (!expiryDate) return 0;
    const expiry = new Date(expiryDate);
    const today = new Date();
    return Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
  };

  useEffect(() => {
    getDoctorDetails();
    getSubscription();
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-tr from-blue-50 to-white">
      <Navbar />

      <div className="pt-24 px-4 pb-12 max-w-5xl mx-auto space-y-6">

        {/* ================= SUBSCRIPTION ================= */}
        {subscription && (
          <div className="rounded-3xl bg-gradient-to-br from-blue-600 to-indigo-600 text-white shadow-2xl p-6">

            {/* HEADER */}
            <div className="flex justify-between">
              <div>
                <p className="text-sm opacity-80">Current Plan</p>
                <h2 className="text-2xl font-bold">
                  {subscription.plan?.toUpperCase()}
                </h2>
              </div>

              <div className="text-right">
                <p className="text-sm opacity-80">Expiry</p>
                <p className="font-semibold">
                  {subscription.is_active
                    ? new Date(subscription.expiry_date).toLocaleDateString()
                    : "Free Plan"}
                </p>
                <span className="text-xs opacity-80">
                  {subscription.is_active
                    ? `${getDaysLeft(subscription.expiry_date)} days left`
                    : "No expiry"}
                </span>
              </div>
            </div>

            <div className="border-t border-white/20 my-5"></div>

            {/* USER CREDITS */}
            <p className="text-sm font-semibold mb-2">
  💊 Prescription Credits
</p>
            <div className="grid grid-cols-3 gap-3 text-center mb-4">
              <div className="bg-white/10 p-3 rounded-xl">
                <p className="text-xs">Total</p>
                <p className="text-lg font-bold">{subscription.total_credits}</p>
              </div>
              <div className="bg-white/10 p-3 rounded-xl">
                <p className="text-xs">Used</p>
                <p className="text-lg font-bold">{subscription.used_credits}</p>
              </div>
              <div className="bg-white/10 p-3 rounded-xl">
                <p className="text-xs">Remaining</p>
                <p className={`text-lg font-bold ${
                  subscription.remaining_credits <= 2
                    ? "text-red-300"
                    : "text-green-300"
                }`}>
                  {subscription.remaining_credits}
                </p>
              </div>
            </div>

            {/* USER PROGRESS */}
            <div className="mb-4">
              <div className="flex justify-between text-xs">
                <span>User Usage</span>
                <span>
                  {subscription.used_credits}/{subscription.total_credits}
                </span>
              </div>
              <div className="w-full h-2 bg-white/20 rounded">
                <div
                  className="h-full bg-green-400"
                  style={{
                    width: `${
                      subscription.total_credits
                        ? (subscription.used_credits /
                            subscription.total_credits) * 100
                        : 0
                    }%`,
                  }}
                ></div>
              </div>
            </div>

            {/* PATIENT CREDITS */}
            <p className="text-sm font-semibold mb-2">🧑 Patient Credits</p>
            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="bg-white/10 p-3 rounded-xl">
                <p className="text-xs">Total</p>
                <p className="text-lg font-bold">
                  {subscription.total_patient_credits}
                </p>
              </div>
              <div className="bg-white/10 p-3 rounded-xl">
                <p className="text-xs">Used</p>
                <p className="text-lg font-bold">
                  {subscription.used_patient_credits}
                </p>
              </div>
              <div className="bg-white/10 p-3 rounded-xl">
                <p className="text-xs">Remaining</p>
                <p className={`text-lg font-bold ${
                  subscription.patient_remaining_credits === 0
                    ? "text-red-300"
                    : "text-yellow-300"
                }`}>
                  {subscription.patient_remaining_credits}
                </p>
              </div>
            </div>

            {/* PATIENT PROGRESS */}
            <div className="mt-4">
              <div className="flex justify-between text-xs">
                <span>Patient Usage</span>
                <span>
                  {subscription.used_patient_credits}/
                  {subscription.total_patient_credits}
                </span>
              </div>
              <div className="w-full h-2 bg-white/20 rounded">
                <div
                  className="h-full bg-yellow-400"
                  style={{
                    width: `${
                      subscription.total_patient_credits
                        ? (subscription.used_patient_credits /
                            subscription.total_patient_credits) * 100
                        : 0
                    }%`,
                  }}
                ></div>
              </div>
            </div>

            {/* WARNINGS */}
            {subscription.remaining_credits <= 2 && (
              <p className="text-yellow-200 mt-3 text-sm">
                ⚠️ Low user credits — upgrade soon
              </p>
            )}

            {subscription.patient_remaining_credits === 0 && (
              <p className="text-red-300 mt-2 text-sm">
                🚨 Patient credits exhausted
              </p>
            )}

            {/* BUTTON */}
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => navigate("/subscription")}
                className="bg-white text-blue-600 px-5 py-2 rounded-xl font-semibold"
              >
                Upgrade Plan 🚀
              </button>
            </div>
          </div>
        )}

        {/* ================= DOCTOR PROFILE ================= */}
        <div className="bg-white rounded-3xl shadow-xl p-6 border">
          {!doctor ? (
            <p className="text-center text-gray-500">Loading...</p>
          ) : (
            <>
              <h2 className="text-2xl font-bold text-blue-800 mb-4 flex gap-2">
                <User /> {doctor.doctor_name}
              </h2>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <p><Mail /> {doctor.doctor_email}</p>
                <p><Phone /> {doctor.phone_no}</p>
                <p><Hospital /> {doctor.clinic_name}</p>
                <p><MapPin /> {doctor.address}</p>
                <p><Shield /> {doctor.registration_no}</p>
                <p><Shield /> {doctor.medical_licence_no}</p>
                <p>🎓 {doctor.degree_name}</p>
                <p><Clock /> {doctor.opd_time}</p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DoctorProfile;

